import { discoverAdjacent } from './discover';
import { canLeaveRoom, getRoom } from './roomRules';
import type { Direction, DungeonFloor } from '../game/types';

export type MoveFailReason =
  | 'dead'
  | 'unknown-room'
  | 'cannot-leave'
  | 'not-connected'
  | 'same-room';

export type MoveResult =
  | { ok: true; floor: DungeonFloor }
  | { ok: false; reason: MoveFailReason };

export function getReachableRoomIds(floor: DungeonFloor, alive: boolean): string[] {
  if (!alive) {
    return [];
  }
  const current = getRoom(floor, floor.playerRoomId);
  if (!current || !canLeaveRoom(current)) {
    return [];
  }
  return current.connections
    .filter((connection) => !connection.isLocked && Boolean(getRoom(floor, connection.targetRoomId)))
    .map((connection) => connection.targetRoomId);
}

function isConnected(floor: DungeonFloor, fromId: string, toId: string): boolean {
  const from = getRoom(floor, fromId);
  if (!from) {
    return false;
  }

  return from.connections.some((c) => c.targetRoomId === toId && !c.isLocked);
}

/** Move to a room id only if connected and leave is allowed. */
export function moveToRoomId(
  floor: DungeonFloor,
  targetRoomId: string,
  alive: boolean,
): MoveResult {
  if (!alive) {
    return { ok: false, reason: 'dead' };
  }

  if (targetRoomId === floor.playerRoomId) {
    return { ok: false, reason: 'same-room' };
  }

  const current = getRoom(floor, floor.playerRoomId);
  if (!canLeaveRoom(current)) {
    return { ok: false, reason: 'cannot-leave' };
  }

  const target = getRoom(floor, targetRoomId);
  if (!target) {
    return { ok: false, reason: 'unknown-room' };
  }

  if (!isConnected(floor, floor.playerRoomId, targetRoomId)) {
    return { ok: false, reason: 'not-connected' };
  }

  const rooms = {
    ...floor.rooms,
    [targetRoomId]: {
      ...target,
      status: 'EXPLORED' as const,
    },
  };

  let next: DungeonFloor = {
    ...floor,
    rooms,
    playerRoomId: targetRoomId,
  };

  next = discoverAdjacent(next, targetRoomId);
  return { ok: true, floor: next };
}

export function moveByDirection(
  floor: DungeonFloor,
  direction: Direction,
  alive: boolean,
): MoveResult {
  if (!alive) {
    return { ok: false, reason: 'dead' };
  }

  const current = getRoom(floor, floor.playerRoomId);
  if (!current) {
    return { ok: false, reason: 'unknown-room' };
  }

  const link = current.connections.find((c) => c.direction === direction && !c.isLocked);
  if (!link) {
    return { ok: false, reason: 'not-connected' };
  }

  return moveToRoomId(floor, link.targetRoomId, alive);
}
